import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import {
  Disclose,
  EmptyState,
  Headline,
  Loading,
  Refusal,
  Tooltip,
} from "@lopie/kernel-ui";

import { ProposeOverrideDialog, ReplayControl } from "./mappingOverrides.jsx";

/** The four kinds `review_queue.py` produces, and how each reads on a card.
 * Rendered from what the kernel's own `kinds` list says exists, not a
 * hardcoded switch — a fifth kind added there needs no change here beyond
 * a label, same test the kernel-ui README states for schema-driven UI. */
const KIND_LABEL = {
  held_run: "Held run",
  held_reject: "Held reject",
  match: "Possible duplicate",
  low_confidence_category: "Low-confidence category",
};

/** `review_queue.HELD_REJECT` — the one kind with a real fix mechanism
 * (LO-172): propose a mapping correction, approve it on Requests, replay. */
const HELD_REJECT = "held_reject";

/** Only kinds `review_queue.accept` actually does something with. The other
 * two always refuse — see the module's own docstring — so this screen does
 * not offer a button that can only ever come back `cannot_accept`. */
const ACCEPTABLE = new Set(["match", "low_confidence_category"]);

/** Just `Organization`: the only type this epic ever wired a dedupe key, a
 * fuzzy score or a `category` field onto (`schema.py`). Offering a picker
 * that mostly answers `unknown_type` or an empty pass would be a control
 * that looks like a choice and is not one. */
const CHECKABLE_TYPE = "Organization";

function QueueCard({ item, onAccept, mayGovern, busy, onProposeOverride, onReplay }) {
  const acceptable = ACCEPTABLE.has(item.kind);
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Chip size="small" label={KIND_LABEL[item.kind] || item.kind} />
          <Typography variant="body2" sx={{ flex: 1, minWidth: "20ch" }}>
            {item.summary}
          </Typography>
        </Stack>

        <Disclose summary="Evidence" label="What this is based on" align="inline">
          {item.kind === HELD_REJECT && item.evidence?.payload ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              Includes the raw payload this reject held onto — a source id
              or a field in it can carry personal data, the same caution
              this console gives reject samples elsewhere.
            </Typography>
          ) : null}
          <Box component="pre" sx={{
            m: 0, p: 1, fontSize: "0.8rem", overflowX: "auto",
            bgcolor: "action.hover", borderRadius: 1,
          }}>
            {JSON.stringify(item.evidence, null, 2)}
          </Box>
        </Disclose>

        {acceptable ? (
          <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
            <Tooltip
              title={
                mayGovern
                  ? "Confirms this as a real duplicate, or applies this " +
                    "category to the record."
                  : "You are not on this kernel's governor list — accepting " +
                    "is a decision this consequential needs a second " +
                    "person for, same as approving a manifest request."
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!mayGovern || busy}
                  onClick={() => onAccept(item)}
                >
                  Accept
                </Button>
              </span>
            </Tooltip>
          </Stack>
        ) : item.kind === HELD_REJECT ? (
          <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
            <Button size="small" variant="outlined" onClick={() => onProposeOverride(item)}>
              Propose a mapping fix
            </Button>
            <ReplayControl source={item.evidence.source_system}
              rule={item.evidence.reason} mayGovern={mayGovern} onReplay={onReplay} />
          </Stack>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Informational only — this kernel has no "looked at this, it's
            fine now" mechanism for a {KIND_LABEL[item.kind] || item.kind}{" "}
            yet. Waiting for the next sync is what resolves it.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

/**
 * What is waiting on a person to decide (LO-149 through LO-159).
 *
 * Four kinds, one list, the same argument `review_queue.py`'s own module
 * docstring makes: a held-anomalous run, a held reject, a possible
 * duplicate and a low-confidence category are different facts produced by
 * different cards, and a person deciding what to do next should not have
 * to learn four shapes to read one queue.
 *
 * ## Two of the four are free, two cost money on click
 *
 * `held_run` and `held_reject` are already persisted — `GET /review-queue`
 * lists them for nothing, the same read as any other screen. `match` and
 * `low_confidence_category` need a live TypeSafe call to produce at all,
 * and neither is stored anywhere, so listing them for free would mean
 * paying for a judgement nobody asked for every time this screen loads.
 * "Check now" is the button that spends that money, once per click, never
 * on page load — see `_review_queue_check`'s own docstring for the same
 * argument made against the route that would otherwise do this silently.
 */
export default function DataQuality({ get, post, onEffective }) {
  const [data, setData] = useState(null);
  const [failure, setFailure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null); // { items, applied, cost } | { error }
  const [acceptBusy, setAcceptBusy] = useState(null); // item.id currently being accepted
  const [overrideItem, setOverrideItem] = useState(null); // held_reject item being proposed against
  const [overrideOutcome, setOverrideOutcome] = useState(null); // { request_id } from a successful propose

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await get("/review-queue"));
      setFailure(null);
    } catch (e) {
      setFailure(e);
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    load();
    // This screen is about held runs, rejects, matches and categories —
    // none of it is one record's tags, so there is no intersection to
    // report. Same call `Connectors` makes and the same reason.
    onEffective?.(null);
  }, [load, onEffective]);

  async function runCheck() {
    setChecking(true);
    setCheckResult(null);
    try {
      const result = await post("/review-queue/check", { type: CHECKABLE_TYPE });
      setCheckResult(result);
    } catch (e) {
      setCheckResult({ error: e?.body?.detail || e?.message || String(e) });
    } finally {
      setChecking(false);
    }
  }

  async function accept(item) {
    setAcceptBusy(item.id);
    try {
      await post("/review-queue/accept", {
        kind: item.kind, id: item.id, evidence: item.evidence,
      });
      // Ephemeral items have nowhere to reload from (see the module
      // docstring) — an accepted one is simply dropped from the pass this
      // click already paid for, rather than re-asked for.
      setCheckResult((prev) => prev && {
        ...prev,
        items: prev.items.filter((i) => i.id !== item.id),
      });
    } catch (e) {
      setCheckResult((prev) => prev && {
        ...prev,
        error: e?.body?.detail || e?.message || String(e),
      });
    } finally {
      setAcceptBusy(null);
    }
  }

  // Thrown errors surface in `ReplayControl`'s own confirm dialog, not here
  // — this screen does not reload afterward, the same ephemeral treatment
  // `accept` gives `checkResult`: a held reject's own row is cleared by the
  // next `GET /review-queue` a person triggers, not patched in place from a
  // response this call cannot fully describe.
  async function replay(source, rule) {
    return post(`/connectors/${encodeURIComponent(source)}/rejects/replay`,
      { rule });
  }

  if (loading && !data) return <Loading label="Reading the queue" />;
  if (failure) {
    return <Refusal failure={failure} actor="This console" onRetry={load} />;
  }

  const items = data?.items || [];
  const mayGovern = Boolean(data?.may_decide);
  const checkItems = checkResult?.items || [];

  return (
    <Stack spacing={2}>
      <Headline
        eyebrow="Waiting on a person"
        figure={String(items.length)}
        caption={`held run${items.length === 1 ? "" : "s"} and held reject${
          items.length === 1 ? "" : "s"}, right now`}
        basis="Counted from this kernel's own held state — a run an anomaly check stopped before it wrote, or a record a mapping fix has not replayed yet. Not a forward guarantee: a count of what is held now, not of what will be."
      />

      {(data?.does_not_prove || []).map((line, i) => (
        <Alert key={i} severity="info" variant="outlined" icon={false}>
          {line}
        </Alert>
      ))}

      {overrideOutcome ? (
        <Alert severity="success" variant="outlined"
          onClose={() => setOverrideOutcome(null)}>
          Proposed. A governor decides it on the Requests screen — see{" "}
          <code>{overrideOutcome.request_id}</code>.
        </Alert>
      ) : null}

      {items.length ? (
        <Stack spacing={1.5}>
          {items.map((item) => (
            <QueueCard key={`${item.kind}:${item.id}`} item={item}
              mayGovern={mayGovern} busy={false}
              onProposeOverride={setOverrideItem} onReplay={replay} />
          ))}
        </Stack>
      ) : (
        <EmptyState title="Nothing held right now">
          No connector has a held-anomalous run or a held reject waiting.
        </EmptyState>
      )}

      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
          Possible duplicates and low-confidence categories
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Neither is free to list — each needs a live TypeSafe call and
          nothing persists them. This runs one bounded pass over{" "}
          <code>{CHECKABLE_TYPE}</code> records, on click, at real cost.
        </Typography>
        <Tooltip
          title={
            mayGovern
              ? "Judges up to 5 uncertain duplicate pairs and categorizes " +
                "up to 5 below-threshold records, right now."
              : "You are not on this kernel's governor list — checking " +
                "spends real money on every click, so it needs a governor " +
                "too, not only the app's own permission."
          }
        >
          <span>
            <Button variant="outlined" disabled={!mayGovern || checking}
              onClick={runCheck}>
              {checking ? "Checking…" : `Check ${CHECKABLE_TYPE} now`}
            </Button>
          </span>
        </Tooltip>

        {checkResult?.error ? (
          <Alert severity="error" variant="outlined" sx={{ mt: 1.5 }}
            onClose={() => setCheckResult(null)}>
            {checkResult.error}
          </Alert>
        ) : null}

        {checkResult && !checkResult.error ? (
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            {checkResult.applied ? (
              <Alert severity="info" variant="outlined">
                {checkResult.applied} record{checkResult.applied === 1 ? "" : "s"}{" "}
                scored above threshold and {checkResult.applied === 1 ? "was" : "were"}{" "}
                categorized automatically — not listed below, because there
                is nothing left to decide about {checkResult.applied === 1 ? "it" : "them"}.
              </Alert>
            ) : null}

            {checkItems.length ? (
              checkItems.map((item) => (
                <QueueCard key={`${item.kind}:${item.id}`} item={item}
                  mayGovern={mayGovern} busy={acceptBusy === item.id}
                  onAccept={accept} />
              ))
            ) : (
              <EmptyState title="Nothing came back uncertain or below threshold">
                This pass found nothing worth a person's time.
              </EmptyState>
            )}

            <Disclose summary="What this pass cost" label="Token spend" align="inline">
              <Box component="pre" sx={{
                m: 0, p: 1, fontSize: "0.8rem", overflowX: "auto",
                bgcolor: "action.hover", borderRadius: 1,
              }}>
                {JSON.stringify(checkResult.cost, null, 2)}
              </Box>
            </Disclose>
          </Stack>
        ) : null}
      </Box>

      <ProposeOverrideDialog
        open={Boolean(overrideItem)}
        source={overrideItem?.evidence?.source_system || ""}
        concept={overrideItem?.evidence?.concept || ""}
        rejectReason={overrideItem?.evidence?.reason || ""}
        payloadKeys={Object.keys(overrideItem?.evidence?.payload || {})}
        post={post}
        onClose={() => setOverrideItem(null)}
        onProposed={(result) => setOverrideOutcome(result)}
      />
    </Stack>
  );
}
